/**
 * Übersetzungs-Funktion der I18n_Infrastruktur.
 *
 * Reine, React-unabhängige Funktionen, damit sie property-testbar bleiben.
 * `translate` löst einen Übersetzungs-Schlüssel über die dreistufige
 * Fallback-Kette (Aktive_Sprache → `de` → key) auf und interpoliert benannte
 * Platzhalter. `interpolate` ersetzt `{name}`-Platzhalter durch die übergebenen
 * Werte.
 */

import { type Catalog, type Language, DEFAULT_LANGUAGE } from "./catalog";

/** Werte für die Platzhalter-Interpolation (Req 3.1). */
export type TranslateParams = Record<string, string | number>;

/**
 * Löst einen Übersetzungs-Schlüssel in der Aktive_Sprache auf.
 *
 * Fallback-Kette:
 * 1. Text der Aktive_Sprache, falls vorhanden (Req 1.3).
 * 2. sonst Text der Standardsprache (`de`), falls vorhanden (Req 1.4).
 * 3. sonst der Übersetzungs-Schlüssel selbst (Req 1.5).
 *
 * Der ermittelte Text wird anschließend interpoliert (Req 3.1).
 */
export function translate(
  catalog: Catalog,
  language: Language,
  key: string,
  params?: TranslateParams,
): string {
  const template =
    catalog[language]?.[key] ?? // Req 1.3
    catalog[DEFAULT_LANGUAGE]?.[key] ?? // Req 1.4
    key; // Req 1.5
  return interpolate(template, params);
}

/**
 * Ersetzt benannte `{name}`-Platzhalter durch `params[name]`.
 *
 * - Platzhalter mit passendem Wert werden durch diesen Wert ersetzt (Req 3.1).
 * - Platzhalter ohne übergebenen Wert bleiben unverändert im Text stehen (Req 3.3).
 * - Übergebene Werte, deren Platzhalter nicht im Template vorkommen, werden
 *   ignoriert; der Text bleibt sonst unverändert (Req 3.2).
 */
export function interpolate(template: string, params?: TranslateParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    // Nur EIGENE Eigenschaften zählen als "übergebener Wert"; geerbte
    // Prototyp-Namen (`toString`, `constructor`, …) dürfen Platzhalter nicht
    // versehentlich ersetzen (Req 3.3).
    Object.prototype.hasOwnProperty.call(params, name)
      ? String(params[name])
      : match,
  );
}
